import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { Eye, Heart, ChatCircle, ArrowRight, MagnifyingGlass, Star } from "@phosphor-icons/react";

export default function Explore() {
  const [authors, setAuthors] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const fetchExplore = async (p = 1, query = "", append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/explore`, { 
        params: { page: p, limit: 10, q: query.trim() || undefined } 
      });
      if (append) {
        setAuthors(prev => [...(prev || []), ...data.authors]);
      } else {
        setAuthors(data.authors || []);
      }
      setMeta(data.meta);
    } catch (e) {
      setErr("Couldn't load the explore feed.");
      setAuthors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchExplore(1, search, false);
    }, search ? 400 : 0);

    return () => clearTimeout(handler);
  }, [search]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchExplore(next, search, true);
  };

  if (authors === null && page === 1) return <div className="max-w-5xl mx-auto px-6 py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="eyebrow text-muted-foreground mb-3">The newsstand</div>
          <h1 className="font-serif text-5xl sm:text-6xl font-black tracking-tight">Explore Inkspot.</h1>
          <p className="mt-4 text-muted-foreground max-w-xl">
            A growing community of essayists. Discover new voices and follow them.
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search articles or authors..."
            className="w-full bg-card border border-border pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-foreground transition-colors"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="explore-search-input"
          />
          {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase font-mono text-muted-foreground">Searching...</div>}
        </div>
      </div>

      {err ? <div className="mt-6 text-sm text-destructive">{err}</div> : null}

      {authors.length === 0 ? (
        <div className="mt-16 border border-dashed border-border p-12 text-center">
          <div className="font-serif text-2xl font-bold">{search ? "No results found" : "No journals yet"}</div>
          <p className="mt-2 text-sm text-muted-foreground">
            {search ? `We couldn't find anything matching "${search}".` : "Be the first to publish on Inkspot."}
          </p>
          {!search && (
            <Link to="/register" className="mt-6 inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 hover:bg-foreground/90" data-testid="explore-cta-register">
              Claim your URL <ArrowRight size={14} weight="bold" />
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-12 space-y-12">
          {authors.map((site, i) => (
            <motion.div
              key={site.author.username}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.08, 0.4) }}
              className="grid grid-cols-12 gap-8 border-b border-border pb-12"
              data-testid={`explore-author-${site.author.username}`}
            >
              <div className="col-span-12 md:col-span-4">
                <Link to={`/${site.author.username}`} className="flex items-center gap-3 group" data-testid={`visit-${site.author.username}`}>
                  {site.author.profile_picture ? (
                    <img src={fileUrl(site.author.profile_picture)} alt="" className="h-12 w-12 object-cover" />
                  ) : (
                    <div className="h-12 w-12 bg-secondary grid place-items-center font-serif text-xl">{(site.author.name || "A")[0]}</div>
                  )}
                  <div>
                    <div className="font-serif text-2xl font-bold leading-tight group-hover:underline underline-offset-4">{site.author.name || site.author.username}</div>
                    <div className="font-mono text-xs text-muted-foreground">/{site.author.username}</div>
                  </div>
                </Link>
                <p className="text-sm text-muted-foreground mt-3 line-clamp-3">
                  {site.author.bio || site.author.site_tagline || `${site.article_count} ${site.article_count === 1 ? "essay" : "essays"} published.`}
                </p>
                <Link to={`/${site.author.username}`} className="mt-4 inline-flex items-center gap-1 link-underline text-sm">
                  Visit journal <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
              <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {site.articles.map((a) => (
                  <Link
                    key={a.id}
                    to={`/${site.author.username}/${a.slug}`}
                    className="group block border border-border bg-card p-4 lift-card"
                    data-testid={`explore-article-${a.slug}`}
                  >
                    <div className="aspect-[16/9] mb-3 overflow-hidden -mx-4 -mt-4 border-b border-border relative">
                      {a.cover_image ? (
                        <img src={fileUrl(a.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      ) : (
                        <div className="w-full h-full bg-secondary" />
                      )}
                      {a.category && (
                        <div className="absolute top-3 left-3 bg-orange-600 text-white text-[10px] font-black uppercase px-3 py-1.5 shadow-lg z-20 tracking-widest">
                          {a.category.name}
                        </div>
                      )}
                      {a.is_featured && (
                        <div className="absolute top-3 right-3 bg-yellow-500 text-black text-[10px] font-black uppercase px-3 py-1.5 flex items-center gap-1 shadow-lg z-10 tracking-widest">
                          <Star size={12} weight="fill" /> Featured
                        </div>
                      )}
                    </div>
                    <div className="eyebrow text-[10px] text-muted-foreground flex items-center gap-1.5">
                      <span>{new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      <span>·</span>
                      <span>{a.reading_time || 1}m read</span>
                    </div>
                    <div className="font-serif text-lg font-bold mt-1 leading-tight group-hover:underline">{a.title}</div>
                    {a.excerpt ? <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.excerpt}</div> : null}
                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Eye size={12} /> {a.views}</span>
                      <span className="flex items-center gap-1"><Heart size={12} /> {a.likes_count}</span>
                      <span className="flex items-center gap-1"><ChatCircle size={12} /> {a.comments_count}</span>
                    </div>
                  </Link>
                ))}
                {site.articles.length === 0 && (
                  <div className="col-span-2 text-sm text-muted-foreground">No articles yet.</div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {meta?.has_more && (
        <div className="mt-16 text-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className="font-serif text-lg font-bold border-b-2 border-foreground pb-1 hover:text-muted-foreground hover:border-muted-foreground transition-colors disabled:opacity-50"
            data-testid="explore-load-more"
          >
            {loading ? "Loading..." : "Load more writers"}
          </button>
        </div>
      )}
    </div>
  );
}
